import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams, Navigate } from 'react-router-dom'

/**
 * A comprehensive route guard component that handles both authentication routing
 * and page access control. This component merges the functionality of AuthRouteGuard
 * and CheckPageAccess into a single, unified component.
 *
 * @param {Object} props - The component properties.
 * @param {React.ReactNode} props.children - The child components to render if access is allowed.
 * @param {string} props.tab - The authentication tab type (login, signup, forgot-password).
 * @param {React.ReactNode} props.element - The element to render for auth routes.
 * @param {string} props.identifier - The identifier for the route to check access for.
 * @param {string} props.fallback - The fallback route where redirect should happen if route is disabled.
 * @returns {React.ReactNode} - The rendered children if access is allowed, or appropriate redirect/fallback.
 */
const RouteGuard = ({
  children,
  tab,
  element,
  identifier,
  fallback
}) => {
  const modalBasedAuth = useSelector(state => state?.uiConfig?.general?.modalBasedAuth)
  const disabledRoutes = useSelector(state => state?.uiConfig?.general?.disabledRoutes || [])
  const sportsBook = useSelector(state => state?.casinoMenu?.gameCategory?.sportsBook)
  const affiliatePageEnabled = useSelector(state => state?.uiConfig?.general?.affiliatePage?.enabled)
  const selectedLanguageCode = useSelector(state => state?.language?.selectedLanguageCode)
  const location = useLocation()
  const navigate = useNavigate()
  const { languageCode } = useParams()

  // Memoize redirect path to avoid recalculation
  const redirectPath = useMemo(() => {
    if (!fallback || !selectedLanguageCode) return '/'
    return fallback.replace(':languageCode', selectedLanguageCode.toLowerCase())
  }, [fallback, selectedLanguageCode])

  // Handle authentication routing (from AuthRouteGuard)
  useEffect(() => {
    if (tab && modalBasedAuth) {
      const params = new URLSearchParams(location.search)
      params.set('auth-modal', tab)
      const lang = (languageCode || 'EN').toLowerCase()
      navigate({ pathname: `/${lang}`, search: params.toString() }, { replace: true })
    }
  }, [modalBasedAuth, tab, location.search, navigate, languageCode])

  // Handle page access control (from CheckPageAccess)
  // Special handling for sportsbook route - check sportsBook?.isActive instead of disabledRoutes
  // Special handling for affiliate route - opt-in via affiliatePage.enabled in tenant config
  const isAccessDenied = useMemo(() => {
    if (!identifier) return false
    if (identifier === 'sportsbook') {
      return !sportsBook?.isActive
    }
    if (identifier === 'affiliate') {
      return !affiliatePageEnabled
    }
    return disabledRoutes.includes(identifier)
  }, [identifier, sportsBook?.isActive, affiliatePageEnabled, disabledRoutes])

  if (isAccessDenied) {
    return <Navigate to={redirectPath} replace />
  }

  // Handle authentication modal routing
  if (tab && modalBasedAuth) {
    return null
  }

  // Return the appropriate element/children
  return element || children || null
}

export default RouteGuard
